/**
 * Item management: create and edit SKUs, and manage grab-bag variants (I-39).
 */

import { useMemo, useState } from 'react';

import * as api from '../lib/api';
import { useInventory } from '../lib/useInventory';
import ItemTable from '../components/ItemTable';

/** Fields the editor exposes. Everything else round-trips untouched. */
interface Draft {
  name: string;
  /** The manufacturer's printed barcode, as typed by the user. Not the id. */
  upc: string;
  price: string;
  count: string;
  purchase_cost: string;
  rank: string;
  tags: string;
  infinite: boolean;
}

const EMPTY: Draft = {
  name: '',
  upc: '',
  price: '0',
  count: '0',
  purchase_cost: '0',
  rank: '-',
  tags: '',
  infinite: false,
};

function toDraft(item: api.Item): Draft {
  return {
    name: item.name ?? '',
    upc: item.upc ?? '',
    price: String(item.price ?? 0),
    count: String(item.count ?? 0),
    purchase_cost: String(item.purchase_cost ?? 0),
    rank: String(item.rank ?? '-'),
    tags: (item.tags ?? []).join(', '),
    infinite: Boolean(item.infinite),
  };
}

function fromDraft(draft: Draft): Partial<api.Item> {
  return {
    name: draft.name.trim(),
    upc: draft.upc.trim(),
    price: Number(draft.price) || 0,
    count: Number(draft.count) || 0,
    purchase_cost: Number(draft.purchase_cost) || 0,
    rank: draft.rank.trim() || '-',
    tags: draft.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    infinite: draft.infinite,
  };
}

export default function ItemsPage() {
  const { db, setDb, databases, items, loading, stale, error, reload } = useInventory();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  // Variant editor
  const [variantLabel, setVariantLabel] = useState('');
  const [variantCount, setVariantCount] = useState('0');
  const [variantPrice, setVariantPrice] = useState('');

  const item = selected ? items[selected] : null;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const entries = Object.entries(items);
    if (!needle) return entries;
    // Match the internal id, the name, and the manufacturer's UPC — a
    // seller reading the barcode off a case expects that to find the item.
    return entries.filter(
      ([upc, entry]) =>
        upc.includes(needle) ||
        (entry.name ?? '').toLowerCase().includes(needle) ||
        (entry.upc ?? '').toLowerCase().includes(needle),
    );
  }, [items, search]);

  function select(upc: string) {
    setSelected(upc);
    setCreating(false);
    setDraft(toDraft(items[upc]));
    setMessage('');
  }

  function startCreate() {
    setSelected(null);
    setCreating(true);
    setDraft(EMPTY);
    setMessage('');
  }

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(success);
      reload();
    } catch (err) {
      setMessage((err as api.ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="toolbar">
        <input
          placeholder="Search items…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={db} onChange={(event) => setDb(event.target.value)}>
          {databases.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <button type="button" className="primary" onClick={startCreate}>
          New item
        </button>
        <button type="button" className="ghost" onClick={reload}>
          Refresh
        </button>
      </div>

      {stale && <p className="warn">Showing cached inventory (offline).</p>}
      {error && <p className="error">{error}</p>}
      {message && <p className="notice">{message}</p>}

      <div className="split">
        <ItemTable
          entries={filtered}
          loading={loading}
          selected={selected}
          onSelect={select}
        />

        <aside className="card">
          {!item && !creating ? (
            <p className="muted">Select an item, or create a new one.</p>
          ) : (
            <>
              <h2>{creating ? 'New item' : item?.name}</h2>
              {item && (
                <p className="muted mono">
                  ID {selected}
                  {item.upc && item.upc !== selected
                    ? ` · manufacturer UPC ${item.upc}`
                    : ''}
                </p>
              )}

              <label>
                Name
                <input
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </label>
              <label>
                Manufacturer UPC
                <input
                  value={draft.upc}
                  onChange={(event) => setDraft({ ...draft, upc: event.target.value })}
                  placeholder="The barcode printed on the item (optional)"
                  inputMode="numeric"
                />
              </label>
              <label>
                Price
                <input
                  type="number"
                  step="0.01"
                  value={draft.price}
                  onChange={(event) => setDraft({ ...draft, price: event.target.value })}
                />
              </label>
              <label>
                Unit stock
                <input
                  type="number"
                  value={draft.count}
                  disabled={draft.infinite}
                  onChange={(event) => setDraft({ ...draft, count: event.target.value })}
                />
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={draft.infinite}
                  onChange={(event) =>
                    setDraft({ ...draft, infinite: event.target.checked })
                  }
                />
                Infinite stock (don&apos;t track count)
              </label>
              <label>
                Purchase cost
                <input
                  type="number"
                  step="0.01"
                  value={draft.purchase_cost}
                  onChange={(event) =>
                    setDraft({ ...draft, purchase_cost: event.target.value })
                  }
                />
              </label>
              <label>
                Rank
                <input
                  value={draft.rank}
                  onChange={(event) => setDraft({ ...draft, rank: event.target.value })}
                />
              </label>
              <label>
                Tags (comma separated)
                <input
                  value={draft.tags}
                  onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
                />
              </label>

              <button
                type="button"
                className="primary"
                disabled={busy || !draft.name.trim()}
                onClick={() =>
                  void run(async () => {
                    if (creating) {
                      const created = await api.createItem(db, fromDraft(draft));
                      setCreating(false);
                      setSelected(created.upc);
                    } else if (selected && item) {
                      // Merge so fields this form does not expose (sell_date,
                      // payout_to, variants, sell_total) survive the save.
                      await api.updateItem(db, selected, {
                        ...item,
                        ...fromDraft(draft),
                      });
                    }
                  }, creating ? 'Item created.' : 'Item saved.')
                }
              >
                {creating ? 'Create item' : 'Save changes'}
              </button>

              {item && (
                <button
                  type="button"
                  className="ghost danger"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Delete "${item.name}"?`)) return;
                    void run(async () => {
                      await api.deleteItem(db, selected!);
                      setSelected(null);
                    }, 'Item deleted.');
                  }}
                >
                  Delete item
                </button>
              )}

              {item && (
                <section className="variants">
                  <h3>Grab-bag variants</h3>
                  <p className="muted small">
                    Extra barcodes on this item, each with its own tag and stock.
                    Print them without names so the bag keeps its surprise.
                  </p>

                  <ul className="variant-list">
                    {(item.variants ?? []).map((variant) => (
                      <li key={variant.upc}>
                        <span>
                          <strong>{variant.label}</strong>{' '}
                          <span className="mono small">{variant.upc}</span> ·{' '}
                          {variant.count} left ·{' '}
                          {variant.price == null
                            ? 'item price'
                            : `$${variant.price.toFixed(2)}`}
                        </span>
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          onClick={() =>
                            void run(
                              () => api.deleteVariant(db, selected!, variant.upc),
                              'Variant removed.',
                            )
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                    {(item.variants ?? []).length === 0 && (
                      <li className="muted">No variants yet.</li>
                    )}
                  </ul>

                  <div className="field-row">
                    <label>
                      Tag
                      <input
                        value={variantLabel}
                        onChange={(event) => setVariantLabel(event.target.value)}
                        placeholder="e.g. Jinx"
                      />
                    </label>
                    <label>
                      Stock
                      <input
                        type="number"
                        min={0}
                        value={variantCount}
                        onChange={(event) => setVariantCount(event.target.value)}
                      />
                    </label>
                    <label>
                      Price override
                      <input
                        type="number"
                        step="0.01"
                        value={variantPrice}
                        onChange={(event) => setVariantPrice(event.target.value)}
                        placeholder="blank = item price"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={busy || !variantLabel.trim()}
                      onClick={() =>
                        void run(async () => {
                          await api.createVariant(db, selected!, {
                            label: variantLabel.trim(),
                            count: Number(variantCount) || 0,
                            price: variantPrice === '' ? null : Number(variantPrice),
                          });
                          setVariantLabel('');
                          setVariantCount('0');
                          setVariantPrice('');
                        }, 'Variant added.')
                      }
                    >
                      Add variant
                    </button>
                  </div>
                </section>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
